import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_NAME_SETTING } from '../../../constants/constantsId';
import { comment, dateDraft, freeBag, timeDraft } from '../../../constants/payData';
import { useFormattingContext } from '../../../context/FormattingContext';
import { selectorsLocale } from '../../../redux/locale/localeReducer';
import service from '../../../services/service';
import { buildLocale } from '../../../utils/buildLocale';
import { setLocaleStorageItem, getLocaleStorageItem, removeFromLocaleStorage } from '../../../utils/localStorageHelper';
import DropdownInput, { IOption } from '../../BasketPage/Components/DropdownInput';
import ShowError from '../../Forms/Components/ShowError';
import { getDeliveryDateInFormat } from '../../../utils/getDeliveryDateInFormat';
import { getAvailableDatesData, getTimeRanges } from '../../../utils/deliveryDates';
import { selectorsOther } from '../../../redux/other/otherReducer';
import useCreateDraft from '../../../useHooks/useCreateDraft';

const strToTime = (str: string) => {
  const [h, m] = str.split(':').map(parseFloat);
  return h * 60 + m;
};

const consistInRange = (from: string, to: string, value: string, isFirst?: boolean) => {
  const fromTime = strToTime(from);
  const toTime = strToTime(to);
  const valueTime = strToTime(value);

  if (isFirst === undefined) {
    return valueTime >= fromTime && valueTime <= toTime;
  }
  if (isFirst) {
    return valueTime > fromTime && valueTime <= toTime;
  } else {
    return valueTime >= fromTime && valueTime < toTime;
  }
};

interface IProps {
  error: string;
  setError: (error: string) => void;
  debCreateDraft: () => void;
}

const DeliveryDataComponent = ({ error, setError, debCreateDraft }: IProps) => {
  const currentTranslate = useSelector(selectorsLocale.getTranslate);
  const createDraft = useCreateDraft();
  const [excludeTime, setExcludeTime] = useState([]);
  const [time, setTime] = useState<any>('');
  const [date, setDate] = useState<Date | null>(null);
  const [commentValue, setCommentValue] = useState('');
  const [excludedDates, setExcludedDates] = useState<string[]>([]);
  const [optionsDate, setOptionsDate] = useState<IOption<Date>[]>([]);
  const [optionsTime, setOptionsTime] = useState<IOption<string>[]>([]);
  const [constantForTimes, setConstantForTimes] = useState<any>(undefined);
  const [formattedOptionsDate, setFormattedOptionsDate] = useState<IOption<Date>[]>([]);
  const [filterTime, setFilterTime] = useState<IOption<string>[]>([]);
  const [isFreeBag, setIsFreeBag] = useState(false);
  const { formatDate } = useFormattingContext();
  const settings = useSelector(selectorsOther.getSetting(DEFAULT_NAME_SETTING));
  const todayDate = new Date();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  tomorrowDate.setHours(0, 0, 0, 0);

  useEffect(() => {
    setLocaleStorageItem(dateDraft, new Date().toString());
    const localeComment = getLocaleStorageItem(comment);
    setCommentValue(localeComment || '');
  }, []);

  useEffect(() => {
    const currentFreeBag = getLocaleStorageItem(freeBag);
    if (currentFreeBag) {
      setIsFreeBag(true);
    }
  }, []);

  useEffect(() => {
    service.getCurrentTime().then((res) => {
      const { constantTimes, optionsDate: optionsDateInit } = getAvailableDatesData(
        DEFAULT_NAME_SETTING,
        res || todayDate,
        settings,
        formatDate,
      );
      setOptionsDate(optionsDateInit);
      setConstantForTimes(constantTimes);

      const currentDate = res ? new Date(res) : todayDate;
      service
        .getExcludedDeliveryDatesForMonth({
          date: currentDate,
          placeId: 1,
          type: 'courier',
        })
        .then((res) => {
          if (res.success) {
            setExcludedDates(res.data);
          }
          setDate(currentDate);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getExtraLabel = (theDate: IOption<Date>): string | null => {
    if (formatDate(todayDate) === theDate.label) {
      return 'heute';
    }
    if (formatDate(tomorrowDate) === theDate.label) {
      return 'morgen';
    }

    return null;
  };

  const makeFilterTime = () => {
    const filteredTimes = optionsTime.filter((t) => {
      return !excludeTime.some((e: any) => {
        const [from, to] = t.value.split(' - ');
        return consistInRange(e.timeFrom, e.timeTo, from, false) || consistInRange(e.timeFrom, e.timeTo, to, true);
      });
    });
    if (filteredTimes?.length) {
      setFilterTime(filteredTimes);
      return;
    }
    if (!date) {
      return;
    }
    const currentExcludedDate = getDeliveryDateInFormat(date);
    excludedDates.push(currentExcludedDate);
    setExcludedDates([...excludedDates]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const makeFormattedOptionsDate = () => {
    const formattedOptions = optionsDate
      .filter((data: IOption<Date>) => {
        const theDate = getDeliveryDateInFormat(data.value);
        return !excludedDates?.length || !excludedDates.includes(theDate);
      })
      .map((data: IOption<Date>) => {
        const extraLabel = getExtraLabel(data);

        if (extraLabel) {
          data.label = `${data.label} (${extraLabel})`;
          return data;
        }

        return data;
      });
    const firstFilteredDateData = formattedOptions?.at(0);
    if (firstFilteredDateData) {
      changeDate(firstFilteredDateData);
    }
    setFormattedOptionsDate(formattedOptions);
  };

  useEffect(() => {
    const currentTime = getLocaleStorageItem(timeDraft);
    if (currentTime && filterTime && filterTime.find((t) => t.value === currentTime)) {
      setTime(currentTime);
    } else {
      removeFromLocaleStorage(timeDraft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    makeFormattedOptionsDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedDates]);

  useEffect(() => {
    makeFilterTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeTime]);

  useEffect(() => {
    if (!filterTime?.find(({ value }) => value === time)) {
      setTime('');
      removeFromLocaleStorage(timeDraft);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTime]);

  useEffect(() => {
    if (date === null) {
      return;
    }
    if (constantForTimes) {
      setOptionsTime(getTimeRanges(date, settings, constantForTimes, DEFAULT_NAME_SETTING));
    }

    service
      .getExcludeTime({
        date: new Date(date),
        placeId: 1,
        type: 'courier',
      })
      .then((res) => {
        if (res.success) {
          setExcludeTime(res.data);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  const changeDate = (value: IOption<Date>) => {
    setDate(value.value);
    setLocaleStorageItem(dateDraft, value.value.toString());
    debCreateDraft();
  };

  const changeTime = (value: IOption<string>) => {
    setTime(value.value);
    setLocaleStorageItem(timeDraft, value.value);
    debCreateDraft();
  };

  const changeComment = (value: string) => {
    setCommentValue(value);
    setLocaleStorageItem(comment, value);
  };

  const getDateActualValue = (theDate: Date | null) => {
    if (!theDate) {
      return '';
    }
    const extraLabel = getExtraLabel({ label: formatDate(theDate), value: theDate });
    return `${formatDate(theDate)}${extraLabel ? ' (' + extraLabel + ')' : ''}`;
  };

  const changeFreeBag = (value) => {
    setIsFreeBag(value);
    if (value) {
      setLocaleStorageItem(freeBag, value);
      return;
    }

    removeFromLocaleStorage(freeBag);
  };

  return (
    <div>
      <div className="pay-block_title">
        <div className="title">
          <span className="number">4</span> {buildLocale(currentTranslate, 'payPageDelivery')}
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-6">
          <div className="form-item">
            <label className="required">{buildLocale(currentTranslate, 'payPageDeliveryDate')}</label>
            <DropdownInput
              value={getDateActualValue(date)}
              options={formattedOptionsDate}
              onSelect={changeDate}
              isInput={false}
              readonly
            />
            <p className="form-note pay-block_delivery-note">{buildLocale(currentTranslate, 'deliveryLimits')}</p>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="form-item">
            <label className="required">{buildLocale(currentTranslate, 'payPageTime')}</label>
            <DropdownInput
              placeholder={filterTime.length === 0 ? `${buildLocale(currentTranslate, 'noPossibility')}` : ''}
              value={time}
              options={filterTime}
              onSelect={changeTime}
              isInput={false}
              readonly
            />
            {error && !time && <ShowError errors={{ time: 'Pflichtfeld' }} name={'time'} />}
          </div>
        </div>
        <div className="col-12">
          <div className="form-item">
            <label>{buildLocale(currentTranslate, 'cartComment')}</label>
            <textarea
              className="form-control"
              value={commentValue}
              onChange={(e) => {
                changeComment(e.target.value);
              }}
              onBlur={debCreateDraft}
            />
          </div>
          <div className="pay-check">
            <p>{buildLocale(currentTranslate, 'payPagePackage')}</p>
            <div className="form-check" onClick={() => {}}>
              <input
                id="free-bag"
                type="checkbox"
                checked={isFreeBag}
                onChange={(e) => {
                  changeFreeBag(e.target.checked);
                  createDraft();
                }}
              />
              <label className="form-check-label" htmlFor="free-bag">
                {buildLocale(currentTranslate, 'freeBag')}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryDataComponent;
